import {useEffect, useRef, useState, useCallback} from "react";

interface WebSocketMessage {
  data: any;
}

const useWebSocket = (url: string | null) => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [lastMessage, setLastMessage] = useState<any | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  const reconnectDelay = 1000;

  const connect = useCallback(() => {
    if (!url) return;

    if (websocketRef.current) {
      websocketRef.current.close();
    }
    const ws = new WebSocket(url);
    ws.onopen = () => {
      setIsConnected(true);
    };

    ws.onmessage = (event: WebSocketMessage) => {
      setLastMessage(event.data);
      setMessages((prev) => [...prev, event.data]);
    };

    ws.onerror = (error) => {};

    ws.onclose = (event) => {
      setIsConnected(false);
      if (url && !event.wasClean) {
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay);
      }
    };

    websocketRef.current = ws;
  }, [url]);

  const sendMessage = useCallback(
      (message: any) => {
        if (isConnected && websocketRef.current) {
          websocketRef.current.send(message);
        } else {
          console.warn("Cannot send message: WebSocket is not connected.");
        }
      },
      [isConnected]
  );

  const closeConnection = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.onclose = null;
      websocketRef.current.close();
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => closeConnection();
  }, [connect, closeConnection]);

  return {isConnected, sendMessage, closeConnection, lastMessage, messages};
};

export default useWebSocket;
